<template>
  <div class="bl-session-base">
    <div
      v-if="pageLoading"
      class="fixed top-0 left-0 grid w-screen h-screen place-items-center bg-light-purple-100"
      :style="{ zIndex: 999 }"
    >
      <div
        class="flex items-center text-4xl font-bold font-logo text-light-purple animate-pulse"
      >
        Blüm Session
      </div>
    </div>
    <div
      v-else
      class="fixed top-0 left-0 flex flex-col w-screen h-screen bg-light-purple-100"
      :style="{ zIndex: 999 }"
    >
      <div
        v-if="session && client"
        id="session-header"
        class="flex flex-row items-center justify-between flex-shrink-0 w-full h-12 gap-4 px-4 border-b lg:h-20 lg:px-12 bg-pure-white border-slate-400"
      >
        <div class="flex items-center gap-4">
          <BlInputButton
            type="plain"
            icon="close"
            @clicked="exitSession"
            class="hidden lg:flex"
          />
          <Avatar
            :name="client.name"
            :status="client.status"
            :size="32"
            class="hidden lg:flex"
          />
          <div class="flex truncate lg:max-w-32" :title="client.name">
            <BlTextSubtitle2 :text="client.name" class="truncate" />
          </div>
          <BlTextBody2
            v-if="!session.user"
            class="hidden italic lg:flex"
            text="Deleted user"
          />
          <div
            v-else
            class="flex-wrap items-center flex-shrink-0 hidden h-8 px-2 truncate border rounded lg:flex bg-success-100 border-success-100 max-w-32"
            :title="session.user.name || session.user.email"
          >
            <BlTextBody2
              class="truncate text-success"
              :text="session.user.name || session.user.email"
            />
          </div>

          <div class="h-8 w-0.5 hidden lg:flex bg-grey-200"></div>
          <div class="flex items-center flex-shrink-0 gap-2">
            <div
              class="bl-time-indicator h-2 w-2 lg:h-3.5 lg:w-3.5 flex-shrink-0 rounded-full"
              :class="{ 'bl-time-indicator-recording': !isEnded }"
              :style="{ backgroundColor: '#EAECF0' }"
            />
            <BlTextBody1
              :text="isEnded ? 'Recorded' : 'Recording'"
              class="flex-shrink-0 hidden lg:flex"
            />
            <BlTextBody1
              class="flex-shrink-0 text-xs lg:text-base"
              :text="recordingTime"
            />
          </div>
          <div class="items-center flex-shrink-0 hidden gap-2 lg:flex">
            <BlIcon icon="calendar-blank" class="w-4 h-4" />
            <BlTextBody1
              :text="
                displayDate(
                  session.start_time || session.created_at,
                  'DD/MM/YYYY'
                )
              "
            />
          </div>
        </div>
        <div class="items-center hidden gap-2 lg:flex">
          <BlInputButton
            label="Share session"
            type="plain"
            @clicked="shareOpened = !shareOpened"
          />
          <BlInputButton
            :label="isEnded ? 'Session has ended' : 'End session'"
            :disabled="isEnded || updatingMeasurementIds.length > 0"
            @clicked="openEndSession"
          />
        </div>
        <BlInputButton
          :label="isEnded ? 'Ended' : 'End'"
          dense
          :disabled="isEnded || updatingMeasurementIds.length > 0"
          class="lg:hidden"
          @clicked="openEndSession"
        />
      </div>

      <div
        class="flex flex-shrink-0 w-full h-12 gap-4 px-6 pt-2 border-b bg-pure-white border-slate-400"
      >
        <router-link
          v-for="tab in tabs"
          :key="tab.target"
          :to="tab.target"
          class="flex items-center justify-center w-full transition-all duration-300 border-b-4"
          :class="[tab.isActive ? 'border-light-purple' : 'border-pure-white']"
        >
          <BlTextSubtitle2 v-if="tab.isActive" :text="tab.label" />
          <BlTextBody1 v-else :text="tab.label" />
        </router-link>
      </div>

      <div class="relative w-full">
        <div
          class="grid content-start gap-4 p-4 lg:p-8 md:grid-cols-2 lg:grid-cols-4"
        >
          <div
            v-if="measurementFixed"
            class="fixed bottom-0 left-0 z-20 flex-shrink-0 w-screen lg:w-auto lg:relative"
            :style="
              viewMode === 'desktop'
                ? { height: 'calc(100vh - 5rem - 3rem - 4rem)' }
                : { height: 'auto' }
            "
          >
            <MeasurementCard
              class="relative w-full h-full"
              :key="measurementFixed.id"
              :measurement="measurementFixed"
              :sessionStatus="session.status"
              :isRunning="runningDurationIds.includes(measurementFixed.id)"
              :count="counter"
              @toggle-updated="onToggleUpdatedMeasurment($event)"
              @toggle-running="onToggleRunningDuration(measurementFixed.id)"
              @toggle-saved="onToggleSavedSbt($event)"
              @exit-session="exitSession"
            />
          </div>
          <div
            class="relative grid content-start gap-6 pt-4 overflow-y-auto -top-4 lg:-top-8 pb-128 lg:pt-8 lg:pb-0 md:grid-cols-2 md:col-span-2 scrollbar-none"
            :class="[
              measurementFixed
                ? 'lg:grid-cols-3 lg:col-span-3'
                : 'lg:grid-cols-4 lg:col-span-4'
            ]"
            :style="{ height: 'calc(100vh - 5rem)' }"
          >
            <div
              v-for="(i, idx) in measurementCount"
              :key="idx"
              class="relative w-full h-full"
              :style="
                viewMode === 'desktop' ? { aspectRatio: '100 / 160' } : {}
              "
            >
              <MeasurementCard
                v-if="measurementsOther[idx]"
                class="w-full h-full"
                :measurement="measurementsOther[idx]"
                :sessionStatus="session.status"
                :isRunning="
                  runningDurationIds.includes(measurementsOther[idx].id)
                "
                :count="counter"
                @toggle-updated="onToggleUpdatedMeasurment($event)"
                @toggle-running="
                  onToggleRunningDuration(measurementsOther[idx].id)
                "
                @toggle-saved="onToggleSavedSbt($event)"
                @exit-session="exitSession"
              />
              <div
                v-else
                class="absolute top-0 left-0 w-full h-full border rounded border-light-purple"
              ></div>
            </div>
          </div>
        </div>
        <div
          v-if="activeTab === 'comments' && session"
          class="absolute top-0 left-0 z-50 flex justify-center w-screen py-6 overflow-y-auto bg-light-purple-100"
          :style="
            viewMode === 'desktop'
              ? { height: 'calc(100vh - 8rem)' }
              : { height: 'calc(100vh - 6rem)' }
          "
        >
          <SessionRecordComments :sessionId="session.id" />
        </div>
      </div>
    </div>
    <!--  -->
    <BlBackdrop
      v-if="endSessionInterceptOpened"
      title="Can't end this session"
      type="error"
      :footer="false"
    >
      <div class="px-4 lg:px-8">
        <div class="flex flex-col gap-1">
          <BlTextBody2
            text="You can't end the session because of the following reason(s):"
          />
          <div
            v-if="runningDurationIds.length"
            class="flex items-center gap-2 px-2"
          >
            <div class="flex-shrink-0 w-1 h-1 rounded bg-slate-800"></div>
            <BlTextBody2 text="Timer is still running" />
          </div>
          <div
            v-if="
              probInterceptMessage === 'minimum_required_trials' ||
                probInterceptMessage === 'both'
            "
            class="flex items-center gap-2 px-2"
          >
            <div class="flex-shrink-0 w-1 h-1 rounded bg-slate-800"></div>
            <BlTextBody2 text="Minimum required probes have not been met" />
          </div>
          <div
            v-if="
              probInterceptMessage === 'selected_and_saved' ||
                probInterceptMessage === 'both'
            "
            class="flex items-center gap-2 px-2"
          >
            <div class="flex-shrink-0 w-1 h-1 rounded bg-slate-800"></div>
            <BlTextBody2 text="Actions for the probing have not been saved" />
          </div>
          <div v-if="unsavedSbtIds.length" class="flex items-center gap-2 px-2">
            <div class="flex-shrink-0 w-1 h-1 rounded bg-slate-800"></div>
            <BlTextBody2 text="The target with SBT hasn't saved its result" />
          </div>
          <BlTextBody2
            text="Please address these issues before ending the session."
          />
        </div>
        <div class="flex justify-end py-4 lg:py-8">
          <BlInputButton
            label="Back to session"
            @clicked="endSessionInterceptOpened = false"
          />
        </div>
      </div>
    </BlBackdrop>
    <!--  -->
    <BlBackdrop
      v-if="emptyMeasurmentsOpened"
      title="It seems you haven't recorded any data"
      :footer="false"
    >
      <div class="px-4 lg:px-8" :style="{ width: '40rem' }">
        <div
          class="flex flex-col-reverse items-center justify-center gap-4 lg:flex-col"
        >
          <div>
            <BlTextBody2
              text="Please note that if you end the session now, the targets will record the data as ''0''. To prevent any data recording, you can deactivate the toggle on each target. Would you like to turn off the toggle for all targets?"
            />
          </div>
          <img
            :src="`/static/empty-measurement.png`"
            class="w-auto h-auto max-w-70 max-h-54 lg:max-h-70"
          />
        </div>
        <div
          class="flex flex-col items-center justify-between gap-4 py-4 lg:py-8 lg:flex-row"
        >
          <BlInputButton
            type="plain"
            label="Cancel"
            @clicked="emptyMeasurmentsOpened = false"
          />
          <div class="flex flex-col items-center gap-4 lg:flex-row">
            <BlInputButton
              type="outlined"
              label="Keep active and end session"
              @clicked="onKeepActiveAndEndSession"
            />
            <BlInputButton
              label="Turn off all and end session"
              @clicked="onTrunOffAllAndEndSession"
            />
          </div>
        </div>
      </div>
    </BlBackdrop>
    <!--  -->
    <BlBackdrop
      v-if="endOpened"
      :title="'Confirm to End Session'"
      :description="'Are you sure you want to end the session?'"
      :label="'Yes'"
      class="bl-session-backdrop"
      :submitLoading="endLoading"
      empty
      @backdrop-close="endOpened = false"
      @backdrop-action="endSession"
    />
    <!--  -->
    <RecommendationPopup
      v-if="recommendationOpened"
      :recommendations="recommendations"
      @close-modal="onCloseRecommendation"
    />
    <!--  -->
    <BlBackdrop
      v-if="shareOpened"
      title="Share session"
      cancelLabel="Close"
      description="Share this session by copying the link or scanning the QR code below."
      class="bl-session-backdrop"
      @backdrop-close="shareOpened = false"
    >
      <div class="w-full px-4 lg:w-160 lg:px-8">
        <div class="bl-session-backdrop-input-wrapper">
          <BlInputText :input="linkInput" />
          <div class="bl-session-backdrop-input-button">
            <BlInputButton
              icon="copy"
              type="outlined"
              @clicked="copyToClipboard"
            />
          </div>
        </div>
        <div class="bl-session-backdrop-code">
          <QrcodeVue :value="linkInput.model" :size="200" level="L" />
        </div>
      </div>
    </BlBackdrop>
    <!--  -->
    <BlBackdrop
      v-if="welcomingMobile"
      cancelLabel=""
      :footer="false"
      @backdrop-close="welcomingMobile = false"
    >
      <div
        class="fixed top-0 left-0 flex items-center justify-center w-full h-screen px-6"
      >
        <div
          class="flex flex-col items-center justify-center max-w-md gap-6 mx-auto text-center"
        >
          <div class="text-4xl font-bold text-light-purple font-logo">
            Blüm
          </div>
          <div class="space-y-3 text-center">
            <BlTextBody1
              :text="'You are about to begin a session with'"
              class="text-grey-400"
            />
            <div class="flex items-center justify-center gap-4 text-center">
              <Avatar :name="client.name" />
              <BlTextBody1 class="text-grey-400" :text="client.name" />
            </div>
          </div>
          <BlInputButton
            label="Start the session"
            class="w-full py-2"
            @clicked="welcomingMobile = false"
          />
        </div>
      </div>
    </BlBackdrop>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { displayDate } from "@/plugins/helper.js";
import QrcodeVue from "qrcode.vue";

import SessionRecordComments from "./comment/RecordComments.vue";
import RecommendationPopup from "./modal/RecommendationPopup.vue";
import MeasurementCard from "./MeasurementCard.vue";

import Avatar from "../Avatar.vue";
import BlTextSubtitle1 from "../BlTextSubtitle1.vue";
import BlTextSubtitle2 from "../BlTextSubtitle2.vue";
import BlTextBody1 from "../BlTextBody1.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlIcon from "../BlIcon.vue";
import BlInputButton from "../BlInputButton.vue";
import BlBackdrop from "../BlBackdrop.vue";
import BlInputText from "../BlInputText.vue";

export default {
  name: "Session",
  components: {
    QrcodeVue,
    SessionRecordComments,
    MeasurementCard,
    RecommendationPopup,
    //
    Avatar,
    BlTextSubtitle1,
    BlTextSubtitle2,
    BlTextBody1,
    BlTextBody2,
    BlIcon,
    BlInputButton,
    BlBackdrop,
    BlInputText
  },
  data() {
    return {
      pageLoading: true,
      //
      counter: 0,
      endOpened: false,
      endSessionInterceptOpened: false,
      probInterceptMessage: "", // minimum_required_trials | selected_and_saved | both
      emptyMeasurmentsOpened: false,
      endLoading: false,
      shareOpened: false,
      welcomingMobile: false,
      linkInput: {
        placeholder: "Link",
        label: "Link",
        model: window && window.location ? window.location.href : "",
        readonly: true
      },
      // to look updating measurement result with debounce
      updatingMeasurementIds: [],
      // to look running Duration type
      runningDurationIds: [],
      // to look unsaved Sbt type
      unsavedSbtIds: [],
      //
      recommendationOpened: false,
      recommendations: []
    };
  },
  computed: {
    ...mapGetters(["client", "targets", "session", "measurements", "viewMode"]),
    isEnded() {
      if (!this.session) return true;
      return (
        this.session.status === "completed" ||
        this.session.status === "cancelled"
      );
    },
    recordingTime() {
      let hours = "00";
      let minutes = "00";
      let seconds = "00";
      seconds = (this.counter % 60).toString().padStart(2, "0");
      if (this.counter >= 60) {
        minutes = Math.floor(this.counter / 60);
        if (this.counter >= 3600) minutes = minutes % 60;
        minutes = minutes.toString().padStart(2, "0");
      }
      if (this.counter >= 3600) {
        hours = Math.floor(this.counter / 3600)
          .toString()
          .padStart(2, "0");
      }
      return hours + " : " + minutes + " : " + seconds;
    },
    activeTab() {
      return this.$route.fullPath.includes("?tab=comments")
        ? "comments"
        : "targets";
    },
    tabs() {
      const slug = this.$route.params.slug;
      return [
        {
          target: `/session/${slug}`,
          isActive: this.activeTab === "targets",
          label: "Targets"
        },
        {
          target: `/session/${slug}?tab=comments`,
          isActive: this.activeTab === "comments",
          label: "Comments"
        }
      ];
    },
    measurementFixed() {
      return this.measurements.find(a => a.is_fixed) || null;
    },
    measurementsOther() {
      return this.measurements
        .filter(a => !a.is_fixed)
        .sort((a, b) => a.position - b.position);
    },
    measurementCount() {
      const length = this.measurementsOther.length;
      if (this.viewMode !== "desktop") return length + (length % 2);
      return length + 24 - (length % 12);
    },
    isAllMeasurementResultEmpty() {
      const isAllEmpty = [];
      const recordMeasurments = this.measurements.filter(i => !i.is_dropped);
      recordMeasurments.forEach(i => {
        let isResultsEmpty = true;
        if (i.type === "Measurement::Percentage") {
          for (let key in i.results) {
            if (i.results[key] !== null) isResultsEmpty = false;
          }
        }
        if (i.type === "Measurement::Probing") {
          const arr = Object.keys(i.results);
          if (arr.length > 0) isResultsEmpty = false;
        }
        if (i.type === "Measurement::Duration") {
          if (i.results["seconds"] > 0) isResultsEmpty = false;
        }
        if (i.type === "Measurement::Pir") {
          for (let key in i.results) {
            if (i.results[key] > 0) isResultsEmpty = false;
          }
        }
        if (i.type === "Measurement::Frequency") {
          if (i.results["score"] > 0) isResultsEmpty = false;
        }
        if (i.type === "Measurement::Prompting") {
          for (let key in i.results) {
            if (i.results[key].score > 0) isResultsEmpty = false;
          }
        }
        if (i.type === "Measurement::Sbt") {
          for (let key in i.results) {
            if (i.results[key].prompt_id) isResultsEmpty = false;
          }
        }
        //
        isAllEmpty.push(isResultsEmpty);
      });
      if (isAllEmpty.length === 0) isAllEmpty.push(false);
      return !isAllEmpty.includes(false);
    }
  },
  watch: {},
  async mounted() {
    this.slug = this.$route.params.slug;
    this.recommendations = [];

    this.pageLoading = true;
    const { success, data } = await this.getSession({ slug: this.slug });
    if (!success) {
      this.$router.push({ path: `/clients` });
    }
    const clientId = data.client_id;
    const sessionId = data.id;
    await this.getClient({ id: clientId });
    await this.getMeasurements({ id: sessionId });
    this.pageLoading = false;

    this.counter = data.current_recording_time[0];
    if (data.status === "ongoing") {
      if (this.viewMode !== "desktop") {
        this.welcomingMobile = true;
      }
      this.counterTimer();
    }
    if (data.status === "draft") {
      const title = `Draft session`;
      const message = `Can't record a darft session.`;
      this.showToast({ title, message, type: "error" });
      this.exitSession();
    }
  },
  destroyed() {
    const app = document.getElementById("app");
    if (app) app.scroll({ top: 0, left: 0 });
  },
  methods: {
    ...mapActions([
      "getClient",
      "getSession",
      "updateSession",
      "getMeasurements",
      "updateMeasurement",
      "updateMeasurementIsDropped",
      "getActionRecommendations",
      "showToast"
    ]),
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    displayDate(date, format) {
      return displayDate({ date, format });
    },
    counterTimer() {
      if (this.counter >= 0) {
        setTimeout(() => {
          this.counter += 1;
          this.counterTimer();
        }, 1000);
      }
    },
    copyToClipboard() {
      if (this.linkInput.model) {
        navigator.clipboard.writeText(this.linkInput.model);
        const title = "Link has been copied to clipboard";
        this.showToast({ title, message: "", type: "success" });
      }
    },
    //
    onToggleDropMeasurement(item) {
      const index = this.measurements.findIndex(a => a.id === item.id);
      if (index > -1) {
        if (this.runningDurationIds.includes(item.id)) {
          this.runningDurationIds = this.runningDurationIds.filter(
            i => i !== item.id
          );
        }
        if (this.unsavedSbtIds.includes(item.id)) {
          this.unsavedSbtIds = this.unsavedSbtIds.filter(i => i !== item.id);
        }

        return this.updateMeasurementIsDropped({
          id: item.id,
          measurement: { is_dropped: !item.is_dropped }
        });
      }
    },
    //
    onToggleUpdatedMeasurment({ id, updated }) {
      if (updated) {
        this.updatingMeasurementIds = this.updatingMeasurementIds.filter(
          i => i !== id
        );
      } else {
        if (!this.updatingMeasurementIds.includes(id)) {
          this.updatingMeasurementIds.push(id);
        }
      }
    },
    onToggleRunningDuration(id) {
      if (this.runningDurationIds.includes(id)) {
        this.runningDurationIds = this.runningDurationIds.filter(i => i !== id);
      } else {
        this.runningDurationIds.push(id);
      }
    },
    onToggleSavedSbt({ id, saved }) {
      if (saved) {
        this.unsavedSbtIds = this.unsavedSbtIds.filter(i => i !== id);
      } else {
        if (!this.unsavedSbtIds.includes(id)) {
          this.unsavedSbtIds.push(id);
        }
      }
    },
    //
    exitSession() {
      this.$router.push({ path: `/clients/${this.session.client_id}/session` });
    },
    showSuccessRecordToast() {
      const title = "Session recorded";
      const message = "Success! The session have been recorded.";
      this.showToast({ title, message, type: "success" });
    },
    //
    openEndSession() {
      const unfinishProbs = this.measurements.filter(i => {
        const type = i.type || (i.target && i.target.type) || "";
        return type.includes("Probing") && !i.submitted_at && !i.is_dropped;
      });
      if (
        this.runningDurationIds.length ||
        unfinishProbs.length ||
        this.unsavedSbtIds.length
      ) {
        if (unfinishProbs.length) {
          let minimumRequiredTrials = false;
          let selectedAndSaved = false;

          unfinishProbs.forEach(i => {
            const answered = Object.keys(i.results).length;
            const trials = i.target.probing_number_of_trial;
            if (answered < trials) minimumRequiredTrials = true;
            else selectedAndSaved = true;
          });

          if (minimumRequiredTrials && selectedAndSaved) {
            this.probInterceptMessage = "both";
          } else if (minimumRequiredTrials) {
            this.probInterceptMessage = "minimum_required_trials";
          } else if (selectedAndSaved) {
            this.probInterceptMessage = "selected_and_saved";
          }
        }

        this.endSessionInterceptOpened = true;
      } else if (this.isAllMeasurementResultEmpty) {
        this.emptyMeasurmentsOpened = true;
      } else {
        this.endOpened = true;
      }
    },
    onKeepActiveAndEndSession() {
      this.emptyMeasurmentsOpened = false;
      this.endOpened = true;
    },
    async onTrunOffAllAndEndSession() {
      this.emptyMeasurmentsOpened = false;
      for (let index = 0; index < this.measurements.length; index++) {
        if (!this.measurements[index].is_dropped) {
          await this.onToggleDropMeasurement(this.measurements[index]);
        }
      }
      setTimeout(() => {
        this.endOpened = true;
      }, 300);
    },
    //
    endSession() {
      this.endLoading = true;
      const params = {
        id: this.session.id,
        session: { status: "completed" }
      };
      this.updateSession(params).then(({ success }) => {
        if (success) {
          this.onRequestRecommendation();
        } else this.endLoading = false;
      });
    },
    //
    onRequestRecommendation() {
      this.getActionRecommendations({
        id: this.session.client_id,
        params: `?page=1&per_page=999&session_id=${this.session.id}`
      }).then(({ success, data }) => {
        this.recommendations = data.action_recommendations;
        if (success && this.recommendations.length) {
          this.recommendationOpened = true;
        } else {
          this.recommendations = [];
          this.showSuccessRecordToast();
          setTimeout(() => {
            this.exitSession();
          }, 1000);
        }
        this.endLoading = false;
        this.endOpened = false;
      });
    },
    onCloseRecommendation() {
      this.recommendationOpened = false;
      this.showSuccessRecordToast();
      setTimeout(() => {
        this.exitSession();
      }, 1000);
    }
  }
};
</script>

<style scoped lang="scss">
.bl-session-base {
  .bl-time-indicator {
    &.bl-time-indicator-recording {
      animation: bl-recording 1s infinite;
    }
  }
  .bl-session-backdrop {
    .bl-session-backdrop-input-wrapper {
      position: relative;
      width: 100%;
      display: flex;
      gap: 0.5rem;
      align-items: flex-end;
    }
    .bl-session-backdrop-code {
      position: relative;
      width: 100%;
      margin-top: 2rem;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/sessions/Session.vue